import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IService, IServiceResponse } from '@bciti/types';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ServiceStateService } from './service-state.abstract.service';
import {
    CreateServiceCategoryPayload,
    CreateServiceStatusPayload,
    Service,
    ServiceCategoryAdminResponse,
    ServiceStatus,
    ServiceStatusAdminResponse,
    CreateUpdateServicePayload,
    UpdateServiceCategoryPayload,
    UpdateServiceStatusPayload,
    UpdateServicePayload,
} from './service-state.model';

@Injectable({
    providedIn: 'root',
})
export class ServiceStateServiceImpl implements ServiceStateService {
    private baseUrl: string = environment.API_GATEWAY_URL;

    constructor(@Inject('HTTP_CLIENT') public http: HttpClient) {}

    public getServices(): Observable<Service[]> {
        return this.http.get(`${this.baseUrl}/service-state/admin/service`).pipe(map((r) => r as Service[]));
    }

    public createService(payload: CreateUpdateServicePayload): Observable<Service> {
        return this.http.post(`${this.baseUrl}/service-state/admin/service`, payload).pipe(map((response) => response as Service));
    }

    public updateService(payload: any): Observable<Service> {
        // TODO: Review payload interface
        const newPayload = JSON.parse(JSON.stringify(payload));

        if (payload.category?._id) {
            newPayload.category = payload.category._id;
        }

        if (payload.availableStatus?._id) {
            newPayload.availableStatus = payload.availableStatus._id;
        }

        if (payload.nonAvailableStatus?._id) {
            newPayload.nonAvailableStatus = payload.nonAvailableStatus._id;
        }

        if (payload.override?.status?._id) {
            newPayload.override.status = payload.override.status._id;
        }

        return this.http.patch(`${this.baseUrl}/service-state/admin/service/${payload._id}`, newPayload).pipe(map((response) => response as CreateUpdateServicePayload));
    }

    public updateServiceOrder(service: Service): Observable<Service[]> {
        const payload = { order: service.order };
        return this.http.patch(`${this.baseUrl}/service-state/admin/service/${service._id}/order`, payload).pipe(map((response) => response as Service[]));
    }

    public deleteService(id: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/service-state/admin/service/${id}`).pipe(map((response) => response as any));
    }

    public duplicateService(id: string): Observable<Service> {
        return this.http.post(`${this.baseUrl}/service-state/admin/service/${id}/copy`, {}).pipe(map((response) => response as Service));
    }

    public getServiceCategories(): Observable<ServiceCategoryAdminResponse[]> {
        return this.http.get(`${this.baseUrl}/service-state/admin/service/categories`).pipe(map((r) => r as ServiceCategoryAdminResponse[]));
    }

    public getServiceStatuses(): Observable<ServiceStatus[]> {
        return this.http.get(`${this.baseUrl}/service-state/admin/service/status`).pipe(map((r) => r as any));
    }

    public createServiceCategory(category: CreateServiceCategoryPayload): Observable<ServiceCategoryAdminResponse> {
        return this.http.post(`${this.baseUrl}/service-state/admin/service/categories`, category).pipe(map((response) => response as ServiceCategoryAdminResponse));
    }

    public updateServiceCategory(category: UpdateServiceCategoryPayload): Observable<ServiceCategoryAdminResponse> {
        return this.http.patch(`${this.baseUrl}/service-state/admin/service/categories/${category._id}`, category).pipe(map((response) => response as ServiceCategoryAdminResponse));
    }

    public deleteServiceCategory(id: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/service-state/admin/service/categories/${id}`).pipe(map((response) => response as any));
    }

    public createServiceStatus(status: CreateServiceStatusPayload): Observable<ServiceStatusAdminResponse> {
        return this.http.post(`${this.baseUrl}/service-state/admin/service/status`, status).pipe(map((response) => response as ServiceStatusAdminResponse));
    }

    public updateServiceStatus(status: UpdateServiceStatusPayload): Observable<ServiceStatusAdminResponse> {
        return this.http.patch(`${this.baseUrl}/service-state/admin/service/status/${status._id}`, status).pipe(map((response) => response as ServiceStatusAdminResponse));
    }

    public deleteServiceStatus(id: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/service-state/admin/service/status/${id}`).pipe(map((response) => response as any));
    }

    public postDocuments(document: any): Observable<string[]> {
        const formData = new FormData();
        formData.append('file', document);

        // FIXME: use /common/admin/fileupload/temp
        return this.http.post(`${this.baseUrl}/requests/fileupload/temp`, formData).pipe(map((response: any) => response as string[]));
    }

    public getService(id: string): Observable<IService> {
        return this.http.get(`${this.baseUrl}/service-state/admin/service/${id}`).pipe(map((response) => response as IService));
    }

    public editService(id: string, payload: IService): Observable<IServiceResponse> {
        return this.http.patch(`${this.baseUrl}/service-state/admin/service/${id}`, payload).pipe(map((response) => response as IServiceResponse));
    }
}

export const SERVICES_SERVICE_PROVIDER = {
    provide: ServiceStateService,
    useClass: ServiceStateServiceImpl,
};
